import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

type Locale = "en" | "id";
type TranslationParams = Record<string, string | number>;
type TranslationValue = string | ((params?: TranslationParams) => string);

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, params?: TranslationParams) => string;
}

const STORAGE_KEY = "locale";

const translations: Record<Locale, Record<string, TranslationValue>> = {
  en: {
    "nav.home": "Home",
    "nav.academy": "Academy",
    "nav.achievements": "Achievements",
    "nav.workshops": "Workshops",
    "nav.workshop": "Workshop",
    "nav.raffle": "Raffle",
    "nav.faucet": "Faucet",
    "nav.profile": "Profile",
    "nav.admin": "Admin",
    "header.menu": "Menu",
    "header.signIn": "Sign In",
    "header.getStarted": "Get Started",
    "header.profile": "Profile",
    "header.myCv": "My CV",
    "header.signOut": "Sign Out",
    "header.search": "Search",
    "header.searchDescription": "Search for content on the platform",
    "header.myClasses": "My Classes",
    "header.achievement": "Achievement",
    "header.mentor": "Mentor",
    "language.toggle": "Language",
    "language.english": "English",
    "language.indonesian": "Bahasa Indonesia",
    "footer.poweredBy": "Powered by",
    "footer.rights": "All rights reserved.",
    "search.placeholder": "Search profiles and classes...",
    "search.searching": "Searching...",
    "search.profiles": "Profiles",
    "search.classes": "Classes",
    "search.class": "Class",
    "search.classroom": "Classroom",
    "search.noResults": "No profiles or classes found",
    "search.tryDifferent": "Try a different search term",
    "search.userFallback": "User {{id}}",
    "home.classes": "Classes",
    "home.workshops": "Workshops",
    "home.viewAll": "View All",
    "home.noClasses": "No classes available yet. Check back soon!",
    "home.browseClasses": "Browse Classes",
    "home.noWorkshops": "No upcoming workshops yet",
    "home.browseWorkshops": "Browse Workshops",
    "home.latestFromX": "Latest from X",
    "home.whosLiveNow": "Who's live now",
    "home.weekendClass2Completion": "Weekend Class 2 Completion",
    "home.badgeViewersCount": "{{count}} {{viewerLabel}} earned this badge",
    "home.badgeViewersCountClickable": "{{count}} {{viewerLabel}} earned this badge - click to view their profile",
    "home.viewer": "viewer",
    "home.viewers": "viewers",
    "home.badgeNone": "No one has earned this badge yet.",
    "home.badgeEarnPrompt": "Watch the SuperteamINDO Weekend Class 2 above to earn this badge!",
    "home.weekendClassViewers": "Weekend Class Viewers",
    "home.badgeEarnPromptWeekend": "Watch the SuperteamINDO Weekend Class above to earn this badge!",
    "home.viewProfileTitle": "View {{name}}'s profile",
    "home.viewerFallback": "Viewer",
    "home.mentorsTitle": "Mentors",
    "home.mentorsDescription": "Meet your guides",
    "home.mentorsCta": "Explore Mentors",
    "home.dementorTitle": "DeMentor AI",
    "home.dementorDescription": "Your AI-powered coding mentor",
    "home.dementorCta": "Chat Now",
    "recording.featured": "Featured Recording",
    "recording.watch": "Watch Recording",
    "recording.previous": "Previous",
    "recording.next": "Next",
    "recording.partLabel": "Part {{number}}",
    "recording.partOf": "{{part}} of {{total}}",
    "common.tbd": "TBD",
    "classes.starts": "Starts {{date}}",
    "classes.students": "{{count}} students",
    "workshops.by": "By {{name}}",
    "workshops.past": "Past",
    "workshops.online": "Online",
    "workshops.offline": "Offline",
    "auth.signInTitle": "Sign In",
    "auth.signInSubtitle": "Sign in to your Superteam Study account",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.forgotPassword": "Forgot password?",
    "auth.signingIn": "Signing in...",
    "auth.signIn": "Sign In",
    "auth.noAccount": "Don't have an account?",
    "auth.signUp": "Sign up",
    "auth.createAccount": "Create Account",
    "auth.signUpSubtitle": "Start your learning journey with Superteam Study",
    "auth.confirmPassword": "Confirm Password",
    "auth.minPassword": "Must be at least 6 characters",
    "auth.creatingAccount": "Creating account...",
    "auth.haveAccount": "Already have an account?",
    "auth.resetPassword": "Reset Password",
    "auth.setNewPassword": "Set New Password",
    "auth.resetEmailPrompt": "We'll email you a reset link",
    "auth.newPasswordPrompt": "Enter a new password for your account",
    "auth.sendResetLink": "Send reset link",
    "auth.backToSignIn": "Back to Sign In",
    "auth.haveToken": "Have a token?",
    "auth.tokenMissingTitle": "This page expects a reset link from your email. Please request a new link.",
    "auth.newPassword": "New password",
    "auth.confirmNewPassword": "Confirm new password",
    "auth.resetting": "Resetting...",
    "auth.resetPasswordAction": "Reset password",
    "auth.sendNewLink": "Send a new link",
    "auth.checkEmailTitle": "Check your email",
    "auth.checkEmailBody": "We've sent a confirmation link to your email address. Please click the link to verify your account.",
    "auth.invalidEmailOrPassword": "Invalid email or password",
    "auth.passwordsDoNotMatch": "Passwords do not match",
    "auth.passwordTooShort": "Password must be at least 6 characters",
    "auth.createAccountFailed": "Failed to create account",
    "auth.emailExists": "An account with this email already exists. Please sign in instead.",
    "auth.resetRequestSuccess": "If an account exists with that email, a reset link has been sent.",
    "auth.resetRequestFailed": "Failed to request password reset",
    "auth.resetTokenMissing": "Reset token is missing. Please use the link from your email.",
    "auth.resetConfirmMismatch": "Passwords do not match.",
    "auth.resetSuccess": "Password reset successful. Redirecting to sign in...",
    "auth.resetFailed": "Failed to reset password",
    "auth.sending": "Sending...",
    "notFound.title": "Page Not Found",
    "notFound.subtitle": "The page you're looking for doesn't exist.",
    "notFound.cta": "Go Home",
    "contact.title": "Contact Us",
    "contact.subtitle": "Send us a message and we'll get back to you as soon as possible.",
    "contact.sentTitle": "Message Sent!",
    "contact.sentBody": "We've received your message and will follow up shortly.",
    "contact.sendAnother": "Send another message",
    "contact.cardTitle": "Send a Message",
    "contact.cardDescription": "Questions, feedback, partnership enquiries - all welcome.",
    "contact.name": "Name",
    "contact.namePlaceholder": "Your name",
    "contact.subject": "Subject",
    "contact.subjectPlaceholder": "Select a topic...",
    "contact.advertising": "Advertising / Partnership",
    "contact.accountIssue": "Account Issue",
    "contact.support": "Technical Support",
    "contact.walletChange": "Wallet Change Request",
    "contact.general": "General Enquiry",
    "contact.other": "Other",
    "contact.message": "Message",
    "contact.messagePlaceholder": "Tell us what's on your mind...",
    "contact.required": "Please enter a message.",
    "contact.error": "Something went wrong. Please try again.",
    "contact.sending": "Sending...",
    "contact.sendMessage": "Send Message",
    "carousel.advertisement": "Advertisement",
    "carousel.previous": "Previous slide",
    "carousel.next": "Next slide",
    "carousel.goToSlide": "Go to slide {{index}}",
  },
  id: {
    "nav.home": "Beranda",
    "nav.academy": "Akademi",
    "nav.achievements": "Pencapaian",
    "nav.workshops": "Workshop",
    "nav.workshop": "Workshop",
    "nav.raffle": "Undian",
    "nav.faucet": "Faucet",
    "nav.profile": "Profil",
    "nav.admin": "Admin",
    "header.menu": "Menu",
    "header.signIn": "Masuk",
    "header.getStarted": "Mulai",
    "header.profile": "Profil",
    "header.myCv": "CV Saya",
    "header.signOut": "Keluar",
    "header.search": "Cari",
    "header.searchDescription": "Cari konten di platform",
    "header.myClasses": "Kelas Saya",
    "header.achievement": "Pencapaian",
    "header.mentor": "Mentor",
    "language.toggle": "Bahasa",
    "language.english": "Inggris",
    "language.indonesian": "Bahasa Indonesia",
    "footer.poweredBy": "Didukung oleh",
    "footer.rights": "Hak cipta dilindungi.",
    "search.placeholder": "Cari profil dan kelas...",
    "search.searching": "Mencari...",
    "search.profiles": "Profil",
    "search.classes": "Kelas",
    "search.class": "Kelas",
    "search.classroom": "Ruang kelas",
    "search.noResults": "Tidak ada profil atau kelas yang ditemukan",
    "search.tryDifferent": "Coba kata kunci lain",
    "search.userFallback": "Pengguna {{id}}",
    "home.classes": "Kelas",
    "home.workshops": "Workshop",
    "home.viewAll": "Lihat Semua",
    "home.noClasses": "Belum ada kelas tersedia. Cek kembali segera!",
    "home.browseClasses": "Jelajahi Kelas",
    "home.noWorkshops": "Belum ada workshop mendatang",
    "home.browseWorkshops": "Jelajahi Workshop",
    "home.latestFromX": "Terbaru dari X",
    "home.whosLiveNow": "Siapa yang live sekarang",
    "home.weekendClass2Completion": "Penyelesaian Weekend Class 2",
    "home.badgeViewersCount": "{{count}} {{viewerLabel}} mendapatkan lencana ini",
    "home.badgeViewersCountClickable": "{{count}} {{viewerLabel}} mendapatkan lencana ini - klik untuk melihat profil",
    "home.viewer": "penonton",
    "home.viewers": "penonton",
    "home.badgeNone": "Belum ada yang mendapatkan lencana ini.",
    "home.badgeEarnPrompt": "Tonton SuperteamINDO Weekend Class 2 di atas untuk mendapatkan lencana ini!",
    "home.weekendClassViewers": "Penonton Weekend Class",
    "home.badgeEarnPromptWeekend": "Tonton SuperteamINDO Weekend Class di atas untuk mendapatkan lencana ini!",
    "home.viewProfileTitle": "Lihat profil {{name}}",
    "home.viewerFallback": "Penonton",
    "home.mentorsTitle": "Mentor",
    "home.mentorsDescription": "Temui pemandu Anda",
    "home.mentorsCta": "Jelajahi Mentor",
    "home.dementorTitle": "DeMentor AI",
    "home.dementorDescription": "Mentor coding bertenaga AI",
    "home.dementorCta": "Mulai Chat",
    "recording.featured": "Rekaman Pilihan",
    "recording.watch": "Tonton Rekaman",
    "recording.previous": "Sebelumnya",
    "recording.next": "Berikutnya",
    "recording.partLabel": "Bagian {{number}}",
    "recording.partOf": "{{part}} dari {{total}}",
    "common.tbd": "TBD",
    "classes.starts": "Mulai {{date}}",
    "classes.students": "{{count}} peserta",
    "workshops.by": "Oleh {{name}}",
    "workshops.past": "Selesai",
    "workshops.online": "Online",
    "workshops.offline": "Offline",
    "auth.signInTitle": "Masuk",
    "auth.signInSubtitle": "Masuk ke akun Superteam Study Anda",
    "auth.email": "Email",
    "auth.password": "Kata sandi",
    "auth.forgotPassword": "Lupa kata sandi?",
    "auth.signingIn": "Sedang masuk...",
    "auth.signIn": "Masuk",
    "auth.noAccount": "Belum punya akun?",
    "auth.signUp": "Daftar",
    "auth.createAccount": "Buat Akun",
    "auth.signUpSubtitle": "Mulai perjalanan belajar Anda dengan Superteam Study",
    "auth.confirmPassword": "Konfirmasi Kata Sandi",
    "auth.minPassword": "Minimal 6 karakter",
    "auth.creatingAccount": "Membuat akun...",
    "auth.haveAccount": "Sudah punya akun?",
    "auth.resetPassword": "Atur Ulang Kata Sandi",
    "auth.setNewPassword": "Atur Kata Sandi Baru",
    "auth.resetEmailPrompt": "Kami akan mengirim tautan reset ke email Anda",
    "auth.newPasswordPrompt": "Masukkan kata sandi baru untuk akun Anda",
    "auth.sendResetLink": "Kirim tautan reset",
    "auth.backToSignIn": "Kembali ke Masuk",
    "auth.haveToken": "Sudah punya token?",
    "auth.tokenMissingTitle": "Halaman ini membutuhkan tautan reset dari email Anda. Silakan minta tautan baru.",
    "auth.newPassword": "Kata sandi baru",
    "auth.confirmNewPassword": "Konfirmasi kata sandi baru",
    "auth.resetting": "Mengatur ulang...",
    "auth.resetPasswordAction": "Atur ulang kata sandi",
    "auth.sendNewLink": "Kirim tautan baru",
    "auth.checkEmailTitle": "Periksa email Anda",
    "auth.checkEmailBody": "Kami telah mengirim tautan konfirmasi ke alamat email Anda. Silakan klik tautan untuk memverifikasi akun Anda.",
    "auth.invalidEmailOrPassword": "Email atau kata sandi tidak valid",
    "auth.passwordsDoNotMatch": "Kata sandi tidak cocok",
    "auth.passwordTooShort": "Kata sandi minimal 6 karakter",
    "auth.createAccountFailed": "Gagal membuat akun",
    "auth.emailExists": "Akun dengan email ini sudah ada. Silakan masuk.",
    "auth.resetRequestSuccess": "Jika ada akun dengan email tersebut, tautan reset telah dikirim.",
    "auth.resetRequestFailed": "Gagal meminta reset kata sandi",
    "auth.resetTokenMissing": "Token reset tidak ditemukan. Gunakan tautan dari email Anda.",
    "auth.resetConfirmMismatch": "Kata sandi tidak cocok.",
    "auth.resetSuccess": "Reset kata sandi berhasil. Mengarahkan ke halaman masuk...",
    "auth.resetFailed": "Gagal mereset kata sandi",
    "auth.sending": "Mengirim...",
    "notFound.title": "Halaman Tidak Ditemukan",
    "notFound.subtitle": "Halaman yang Anda cari tidak ada.",
    "notFound.cta": "Kembali ke Beranda",
    "contact.title": "Hubungi Kami",
    "contact.subtitle": "Kirim pesan dan kami akan segera menghubungi Anda.",
    "contact.sentTitle": "Pesan Terkirim!",
    "contact.sentBody": "Kami telah menerima pesan Anda dan akan menindaklanjuti segera.",
    "contact.sendAnother": "Kirim pesan lain",
    "contact.cardTitle": "Kirim Pesan",
    "contact.cardDescription": "Pertanyaan, masukan, atau kerja sama - semua diterima.",
    "contact.name": "Nama",
    "contact.namePlaceholder": "Nama Anda",
    "contact.subject": "Subjek",
    "contact.subjectPlaceholder": "Pilih topik...",
    "contact.advertising": "Iklan / Kemitraan",
    "contact.accountIssue": "Masalah Akun",
    "contact.support": "Dukungan Teknis",
    "contact.walletChange": "Permintaan Perubahan Dompet",
    "contact.general": "Pertanyaan Umum",
    "contact.other": "Lainnya",
    "contact.message": "Pesan",
    "contact.messagePlaceholder": "Ceritakan apa yang Anda pikirkan...",
    "contact.required": "Silakan tulis pesan.",
    "contact.error": "Terjadi kesalahan. Silakan coba lagi.",
    "contact.sending": "Mengirim...",
    "contact.sendMessage": "Kirim Pesan",
    "carousel.advertisement": "Iklan",
    "carousel.previous": "Slide sebelumnya",
    "carousel.next": "Slide berikutnya",
    "carousel.goToSlide": "Ke slide {{index}}",
  },
};

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

const getInitialLocale = (): Locale => {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "en" || stored === "id") {
    return stored;
  }
  const browserLanguage = window.navigator.language?.toLowerCase() || "";
  if (browserLanguage.startsWith("id")) {
    return "id";
  }
  return "en";
};

const interpolate = (template: string, params?: TranslationParams): string => {
  if (!params) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    const value = params[key];
    return value === undefined ? match : String(value);
  });
};

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(getInitialLocale);

  const setLocale = useCallback((nextLocale: Locale) => {
    setLocaleState(nextLocale);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, nextLocale);
    }
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const t = useCallback(
    (key: string, params?: TranslationParams) => {
      const entry = translations[locale][key] ?? translations.en[key];
      if (!entry) return key;
      const value = typeof entry === "function" ? entry(params) : entry;
      return interpolate(value, params);
    },
    [locale]
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
}
